// Rules shared by the camera pages (app/screens/cam/*). Cameras are EZVIZ
// devices: the HASHh API hands out the EZVIZ access token and keeps each
// home's verification codes; the device and camera lists come from EZVIZ's
// open API, called from the browser as the app calls it.

// EZVIZ answers an account only in its own region; the token route says which
// (areaDomain). The app and the viewer use the Singapore one.
const EZVIZ_AREA = "https://isgpopen.ezvizlife.com";
const VIEWER_URL = "https://hikvision.hashh.io";
// EZVIZ pages its device list (at most 50 a page).
const PAGE_SIZE = 50;
const MAX_PAGES = 20;

// The token route answers EZVIZ's token reply, { data: { accessToken, expireTime } }
// (the app keeps its data and reads accessToken). null: no camera account.
export const accessTokenOf = (response) => response?.data?.accessToken ?? response?.accessToken ?? null;

// The region the token belongs to, e.g. https://iindiaopen.ezvizlife.com.
export const areaDomainOf = (response) =>
  /^https:\/\/[a-z0-9.-]+\.ezvizlife\.com$/.test(response?.data?.areaDomain ?? "") ? response.data.areaDomain : EZVIZ_AREA;

// Verification codes by serial, from the codes route's { _id, name, hikvision }.
export const codesOf = (response) =>
  response?.hikvision && typeof response.hikvision === "object" ? response.hikvision : {};

// EZVIZ replies { code: "200", msg, data, page? }; any other code is a failure
// whose msg says why.
export class EzvizError extends Error {}

const ezviz = async (path, fields, area = EZVIZ_AREA) => {
  const response = await fetch(`${area}/api/lapp/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
    body: new URLSearchParams(fields).toString(),
  });
  const body = await response.json();
  if (body?.code !== "200") throw new EzvizError(body?.msg || "EZVIZ refused the request");
  return body;
};

// Every camera device on the account (CameraDevices.js's device/list), all pages.
export const listEzvizDevices = async (accessToken, area) => {
  const devices = [];
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const body = await ezviz("device/list", { accessToken, pageStart: page, pageSize: PAGE_SIZE }, area);
    const rows = Array.isArray(body.data) ? body.data : [];
    devices.push(...rows);
    const total = Number(body.page?.total);
    if (!rows.length || !(total > devices.length)) break;
  }
  return devices;
};

// The cameras (channels) of one device (CameraList.js's device/camera/list).
export const listEzvizCameras = async (accessToken, deviceSerial, area) => {
  const body = await ezviz("device/camera/list", { accessToken, deviceSerial }, area);
  return Array.isArray(body.data) ? body.data : [];
};

// The EZVIZ token is for HASHh's whole camera account, so its device list
// holds every home's cameras: show only the ones this home added (those with
// a verification code saved on it).
export const camerasOfHome = (devices, codes) =>
  devices.filter((device) => Object.prototype.hasOwnProperty.call(codes, device?.deviceSerial));

const base64 = (text) => {
  try {
    return btoa(String(text ?? ""));
  } catch (error) {
    return "";
  }
};

// The hosted viewer CameraList.js opens: token, serial and code, each base64,
// the account's region (AD), which the viewer plays from (without it,
// Singapore), and the channel (CH, a number; 1 when left out). They go after
// "#", so they never reach the viewer's server or its logs.
export const viewerUrl = (accessToken, deviceSerial, verificationCode, area, channelNo) =>
  `${VIEWER_URL}/#AT=${base64(accessToken)}&DS=${base64(deviceSerial)}&VC=${base64(verificationCode)}` +
  (area ? `&AD=${base64(area)}` : "") +
  (Number(channelNo) > 0 ? `&CH=${Number(channelNo)}` : "");

export const deviceNameOf = (device) => device?.deviceName || device?.name || device?.deviceSerial || "Camera";

// The add and delete routes pass on EZVIZ's reply; code "200" is success.
export const succeeded = (response) => String(response?.code) === "200";

// The camera brands that can be added: each goes through the EZVIZ open
// platform with the serial and verification code on the camera's label.
// A brand integrated later is one more entry here.
export const CAMERA_BRANDS = [
  { key: "hikvision", name: "Hikvision", hint: "Hik-Connect cameras, NVRs and DVRs" },
  { key: "ezviz", name: "EZVIZ", hint: "EZVIZ cameras and doorbells" },
];
